// src/pages/exams/exams-metrics/PlayfulTeacher/index.jsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { TiaBanner } from '@/components/custom/tia-banner';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { ExamResultRowTeacher } from './ExamResultRowTeacher';

function getApprovedCount(examStudents) {
  return examStudents.filter(item => item?.user?.['question-books-users']?.[0]?.status === 'approved').length;
}

// Mesma regra de cálculo dos KPIs do Default (ExamMetricsKpis.jsx) — usa
// sempre o índice [0] de question-books-users (não a última tentativa por
// data, ao contrário da linha do aluno), replicado de propósito pra manter
// os números idênticos aos do Default.
function getAverage(examStudents, field) {
  const total = examStudents.reduce((acc, curr) => parseFloat(acc) + parseFloat(curr?.user?.['question-books-users']?.[0]?.[field]), 0);
  return (total / examStudents.length).toFixed(2);
}

function buildKpiItems(examStudents) {
  const averageScore = getAverage(examStudents, 'score');
  const averageScorePercentage = getAverage(examStudents, 'score-percentage');

  return [
    { label: 'Participação', value: examStudents.length },
    { label: 'Aprovados', value: getApprovedCount(examStudents) },
    { label: 'Média global', value: isNaN(averageScore) ? '0.00' : averageScore },
    // Mesmo nome usado no Default (ExamMetricsKpis.jsx, id 5) — não é "taxa
    // de aprovação", é a média de score-percentage entre as tentativas.
    { label: 'Índice de acerto', value: `${averageScorePercentage}%` }
  ];
}

// Mesma integração de dados da tela Default (AdminMetrics.jsx) —
// question_books/:id e question_books_users/reports?filter[question_book_id].
// O layout é outro, mas os dados e o cálculo dos KPIs são os mesmos.
export function ResultadosPlayfulTeacher() {
  const client = useClient();
  const { id: examId } = useParams();

  const [exam, setExam] = useState(null);
  const [examStudents, setExamStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [examResponse, studentsResponse] = await Promise.all([client.fetch(`question_books/${examId}`), client.fetch(`question_books_users/reports?filter[question_book_id]=${examId}`)]);

        if (examResponse.error) {
          toast.error('Erro ao carregar dados da prova');
        } else {
          setExam(examResponse.data);
        }

        if (studentsResponse.error) {
          toast.error('Erro ao carregar dados da prova');
        } else {
          setExamStudents(studentsResponse.data || []);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [examId]);

  // Mesma regra do Default: simulado (visibility === 'open') volta pra
  // "Simulados" no breadcrumb, prova volta pra "Provas".
  const isMock = exam?.visibility === 'open';

  const breadCrumbs = {
    title: 'Ferramentas de Avaliação',
    nav: [
      { route: isMock ? '/avaliacoes/simulados' : '/avaliacoes/provas', name: isMock ? 'Simulados' : 'Provas', isActive: false },
      { route: `/resultados-prova/${examId}`, name: exam?.title ? `Resultados: ${exam.title}` : 'Resultados', isActive: true }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadCrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-28"
        image={JOINHA}
        imageAlt="Personagem do professor"
        imageHeight={215}
        text={exam?.title ? `Resultados de "${exam.title}"! Acompanhe o desempenho da turma. 📊` : 'Resultados da prova! Acompanhe o desempenho da turma. 📊'}
      />

      {loading && <Loader />}

      {!loading && examStudents.length === 0 && (
        <EmptyState
          type="data"
          variant="plain"
          title="Nenhum estudante respondeu esta prova."
          text="Infelizmente não recebemos respostas para essa avaliação."
          className="pt-16 sm:pt-32"
        />
      )}

      {!loading && examStudents.length > 0 && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {buildKpiItems(examStudents).map(item => (
              <div
                key={item.label}
                className="rounded-2xl border border-neutral-100 bg-white p-4 text-center shadow-card"
              >
                <p
                  className="mb-1 text-2xl font-extrabold"
                  style={{ color: '#222731' }}
                >
                  {item.value}
                </p>
                <p className="text-xs font-semibold text-neutral-500">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            {examStudents.map((examStudent, index) => (
              <ExamResultRowTeacher
                key={examStudent.id}
                examStudent={examStudent}
                exam={exam}
                index={index}
              />
            ))}
          </div>
        </>
      )}
    </DashboardContent>
  );
}
